import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

interface GuardCalendarProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  minDate: Date;
  maxDate: Date;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthCells(month: Date): Date[] {
  const firstDay = startOfMonth(month);
  const startWeekday = firstDay.getDay();
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - startWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + index);
    return cellDate;
  });
}

export const GuardCalendar: React.FC<GuardCalendarProps> = ({ selectedDate, onSelectDate, minDate, maxDate }) => {
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(selectedDate));

  useEffect(() => {
    setVisibleMonth(startOfMonth(selectedDate));
  }, [selectedDate]);

  const today = useMemo(() => new Date(), []);
  const cells = useMemo(() => buildMonthCells(visibleMonth), [visibleMonth]);
  const minMonth = useMemo(() => startOfMonth(minDate), [minDate]);
  const maxMonth = useMemo(() => startOfMonth(maxDate), [maxDate]);
  const canGoPreviousMonth = visibleMonth.getTime() > minMonth.getTime();
  const canGoNextMonth = visibleMonth.getTime() < maxMonth.getTime();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '22px',
        border: '1px solid rgba(147, 197, 253, 0.5)',
        bgcolor: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(18px)',
        boxShadow: '0 24px 45px rgba(37, 99, 235, 0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <IconButton
          size="small"
          aria-label="上个月"
          disabled={!canGoPreviousMonth}
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))}
          sx={{ border: '1px solid #dbeafe', bgcolor: '#f8fbff' }}
        >
          <ChevronLeftRoundedIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: '#1e3a8a', fontSize: '0.95rem' }}>
          {visibleMonth.getFullYear()}年{visibleMonth.getMonth() + 1}月
        </Typography>
        <IconButton
          size="small"
          aria-label="下个月"
          disabled={!canGoNextMonth}
          onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))}
          sx={{ border: '1px solid #dbeafe', bgcolor: '#f8fbff' }}
        >
          <ChevronRightRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75, mb: 0.75 }}>
        {['日', '一', '二', '三', '四', '五', '六'].map((label) => (
          <Typography
            key={label}
            align="center"
            sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75 }}>
        {cells.map((cellDate) => {
          const inMonth = cellDate.getMonth() === visibleMonth.getMonth();
          const selected = isSameDay(cellDate, selectedDate);
          const isToday = isSameDay(cellDate, today);
          const disabled = cellDate.getTime() < minDate.getTime() || cellDate.getTime() > maxDate.getTime();

          return (
            <Box
              key={cellDate.toISOString()}
              component="button"
              type="button"
              aria-label={`${cellDate.getMonth() + 1}月${cellDate.getDate()}日`}
              aria-disabled={disabled ? 'true' : 'false'}
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onSelectDate(new Date(cellDate));
                }
              }}
              sx={{
                height: 32,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                fontSize: '0.82rem',
                fontWeight: selected ? 800 : 600,
                color: selected ? '#ffffff' : disabled ? '#cbd5e1' : inMonth ? '#334155' : '#cbd5e1',
                bgcolor: selected
                  ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                  : disabled
                    ? 'rgba(255,255,255,0.5)'
                  : isToday
                    ? 'rgba(59, 130, 246, 0.12)'
                    : 'rgba(255,255,255,0.7)',
                background: selected
                  ? 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)'
                  : undefined,
                border: selected
                  ? '1px solid rgba(37, 99, 235, 0.55)'
                  : disabled
                    ? '1px solid rgba(226, 232, 240, 0.9)'
                  : isToday
                    ? '1px solid rgba(96, 165, 250, 0.45)'
                    : '1px solid rgba(219, 234, 254, 0.75)',
                boxShadow: selected ? '0 10px 18px rgba(37, 99, 235, 0.24)' : 'none',
                transition: 'all 0.18s ease',
                '&:hover': {
                  transform: disabled ? 'none' : 'translateY(-1px)',
                  borderColor: disabled ? 'rgba(226, 232, 240, 0.9)' : 'rgba(96, 165, 250, 0.8)',
                },
              }}
            >
              {cellDate.getDate()}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
